import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Form, FormGroup, Input, InputGroup, Row, UncontrolledAlert, UncontrolledDropdown, UncontrolledTooltip } from "reactstrap";
import SimpleBar from "simplebar-react";
import EmojiPicker from "emoji-picker-react";
import { Link } from "react-router-dom";

import Spinners from "Components/Common/Spinner";
import { Message } from "services/chatbotApi";
import { chatWebSocket } from "services/chatWebSocket";
import Chat from "pages/Chat/Chat";
import "./ChatBot.css";
import ReactMarkdown from 'react-markdown';

interface Props {
    Chat_Title: string;
    Chat_Box_Bot_Status: string;
    messages: Message[];
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
    loading: boolean;
    sendMessage: (content: string, messageType?: string) => void;
    handleTyping: (isTyping: boolean) => void;
    isTyping: boolean;
    currentSessionId: string | null;
    assistantName?: string;
}

const UserBotChat: React.FC<Props> = ({ 
    Chat_Title, 
    Chat_Box_Bot_Status, 
    messages,
    setMessages,
    loading, 
    sendMessage, 
    handleTyping, 
    isTyping, 
    currentSessionId,
    assistantName,
}) => {
    const scrollRef = useRef<any>(null);
    const [isLoading, setLoading] = useState(loading);
    const [copyMsgAlert, setCopyMsgAlert] = useState<boolean>(false);
    const [search_Menu, setSearch_Menu] = useState<boolean>(false);
    const [settings_Menu, setSettings_Menu] = useState<boolean>(false);
    const [other_Menu, setOther_Menu] = useState<boolean>(false);
    const [emoji, setEmoji] = useState<boolean>(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [curMessage, setCurMessage] = useState<string>("");
    const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
    const [localTyping, setLocalTyping] = useState<boolean>(false);
    const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
    const [isRecording, setIsRecording] = useState<boolean>(false);
    const [recordingTime, setRecordingTime] = useState<number>(0);
    const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
    const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
    const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const connectionStatusClass = (() => {
        switch (Chat_Box_Bot_Status) {
            case 'online':
                return 'text-success';
            case 'connecting':
                return 'text-warning';
            case 'disconnected':
                return 'text-danger';
            default:
                return 'text-muted';
        }
    })();
    const connectionStatusLabel = Chat_Box_Bot_Status || 'offline';
    useEffect(() => {
        setLoading(loading);
    }, [loading]);

    // Toggle functions
    const toggleSearch = () => setSearch_Menu(!search_Menu);
    const toggleSettings = () => setSettings_Menu(!settings_Menu);
    const toggleOther = () => setOther_Menu(!other_Menu);

    // Auto-scroll to bottom when new messages arrive
    // Only auto-scroll if a new message is appended (not on full replace)
    const prevMessagesLength = useRef<number>(messages.length);
    useEffect(() => {        
        if (scrollRef.current) {
            scrollRef.current.getScrollElement().scrollTop = scrollRef.current.getScrollElement().scrollHeight;
        }
        prevMessagesLength.current = messages.length;
    }, [messages]);

    // Format time for display
    const formatTime = (timestamp: string) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Copy message functionality
    const copyMsg = (ele: any) => {
        var copyText = ele.closest(".conversation-list").querySelector("p").innerHTML;
        navigator.clipboard.writeText(copyText);
        setCopyMsgAlert(true);
        setTimeout(() => {
            setCopyMsgAlert(false);
        }, 1000);
    };

    // Search functionality
    const handelSearch = () => {
        const searchInput = document.getElementById("searchMessage") as HTMLInputElement;
        const searchFilter = searchInput.value.toUpperCase();
        const searchUL = document.getElementById("users-conversation") as HTMLInputElement;
        const searchLI = searchUL.getElementsByTagName("li");

        Array.prototype.forEach.call(searchLI, (search: HTMLElement) => {
            const a = search.getElementsByTagName("p")[0] || '';
            const txtValue = a.textContent || a.innerText || '';

            if (txtValue.toUpperCase().indexOf(searchFilter) > -1) {
                search.style.display = "";
            } else {
                search.style.display = "none";
            }
        });
    };

    // Emoji picker
    const [emojiArray, setEmojiArray] = useState<string[]>([]);
    const onEmojiClick = (emojiData: any) => {
        const emojiChar = emojiData.emoji;
        setCurMessage(prev => prev + emojiChar);
        adjustTextareaHeight();
        // Don't close emoji picker automatically - let user close it manually
    };
    
    // Close emoji picker when clicking outside
    const emojiButtonRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (emoji && 
                !target.closest('.emoji-picker-react') && 
                !target.closest('.emoji-picker-container') &&
                !emojiButtonRef.current?.contains(target)) {
                setEmoji(false);
            }
        };
        
        if (emoji) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [emoji]);

    // File upload handling
    const handleFileChange = (event: any) => {
        event.preventDefault();
        const files = Array.from(event.target.files) as File[];
        
        // Separate images and other files
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        const otherFiles = files.filter(file => !file.type.startsWith('image/'));
        
        if (imageFiles.length > 0) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result as string);
            };
            reader.readAsDataURL(imageFiles[0]);
        }
        
        if (otherFiles.length > 0) {
            setAttachedFiles(prev => [...prev, ...otherFiles]);
        }
    };

    // Remove attached file
    const removeAttachedFile = (index: number) => {
        setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Auto-resize textarea
    const adjustTextareaHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            const scrollHeight = textareaRef.current.scrollHeight;
            const maxHeight = 150; // Max height in pixels (approximately 6 lines)
            textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
        }
    };

    // Voice recording functions
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            const chunks: Blob[] = [];

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunks.push(event.data);
                }
            };

            recorder.onstop = () => {
                const audioBlob = new Blob(chunks, { type: 'audio/webm' });
                // For now, we'll convert to base64 and send as text
                // In production, you'd upload to server
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64Audio = reader.result as string;
                    // Send voice message
                    sendMessage(base64Audio, 'audio');
                };
                reader.readAsDataURL(audioBlob);
                
                stream.getTracks().forEach(track => track.stop());
            };

            recorder.start();
            setMediaRecorder(recorder);
            setAudioChunks([]);
            setIsRecording(true);
            setRecordingTime(0);

            // Start timer
            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

        } catch (error) {
            console.error('Error accessing microphone:', error);
            alert('Unable to access microphone. Please check permissions.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorder && isRecording) {
            mediaRecorder.stop();
            setIsRecording(false);
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
                recordingIntervalRef.current = null;
            }
            setRecordingTime(0);
        }
    };

    // Format recording time
    const formatRecordingTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Cleanup recording on unmount
    useEffect(() => {
        return () => {
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
            }
            if (mediaRecorder) {
                mediaRecorder.stop();
            }
        };
    }, [mediaRecorder]);

    // Handle typing with debounce
    const handleInputChange = (value: string) => {
        setCurMessage(value);
        adjustTextareaHeight();
        
        // Start typing indicator
        if (value.length > 0) {
            handleTyping(true);
            
            // Clear existing timeout
            if (typingTimeout) {
                clearTimeout(typingTimeout);
            }
            
            // Set new timeout to stop typing after 1 second of inactivity
            const timeout = setTimeout(() => {
                handleTyping(false);
            }, 1000);
            
            setTypingTimeout(timeout);
        } else {
            handleTyping(false);
        }
    };

    // Send message
    const addMessage = () => {
        if (curMessage.trim() !== "") {
            sendMessage(curMessage.trim());
            
            // Reset form
            setCurMessage("");
            setEmoji(false);
            handleTyping(false);
            
            // Reset textarea height
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
            
            // Show local typing indicator immediately after sending message
            setLocalTyping(true);
            setTimeout(() => {
                setLocalTyping(false);
            }, 3000); // Show for 3 seconds
        }
    };

    // Handle Enter key: Enter adds newline; Ctrl/Cmd+Enter sends
    const onKeyPress = (e: any) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            addMessage();
        }
    };

    // Clear typing timeout on unmount
    useEffect(() => {
        return () => {
            if (typingTimeout) {
                clearTimeout(typingTimeout);
            }
        };
    }, [typingTimeout]);

    // Clean up local typing timeout on unmount
    useEffect(() => {
        return () => {
            setLocalTyping(false);
        };
    }, []);

    // Normalize markdown to avoid excessive blank lines from the AI
    const normalizeMarkdown = (text: string) => {
        // Collapse 3+ consecutive newlines into just 2
        const collapsed = text.replace(/\n{3,}/g, "\n\n");
        // Trim leading / trailing whitespace
        return collapsed.trim();
    };

    return (
        <React.Fragment>
            <style>
                {`
                    @keyframes typing {
                        0%, 60%, 100% {
                            transform: translateY(0);
                            opacity: 0.5;
                        }
                        30% {
                            transform: translateY(-10px);
                            opacity: 1;
                        }
                    }
                `}
            </style>
            <div className="w-100 user-chat">
                <Card>
                    <div className="p-4 border-bottom">
                        <Row>
                            <Col md={4} xs={9}>
                                <h5 className="font-size-15 mb-1 text-capitalize">{Chat_Title || "Select a chat"}</h5>
                                <p className="text-muted mb-0" style={{ fontSize: '12px', lineHeight: 1.2 }}>
                                    <i className={`mdi mdi-circle ${connectionStatusClass} align-middle me-1`} style={{ fontSize: '10px', verticalAlign: 'middle' }}></i>
                                    <span style={{ fontSize: '12px', verticalAlign: 'middle', textTransform: 'capitalize' }}>
                                        {connectionStatusLabel}
                                    </span>
                                </p>
                            </Col>
                            <Col md={8} xs={3}>
                                <ul className="list-inline user-chat-nav text-end mb-0">
                                    <li className="list-inline-item">
                                        <Dropdown isOpen={search_Menu} toggle={toggleSearch}>
                                            <DropdownToggle className="btn nav-btn" tag="a">
                                                <i className="bx bx-search-alt" />
                                            </DropdownToggle>
                                            <DropdownMenu className="dropdown-menu-end">
                                                <Form className="px-3 py-2">
                                                    <FormGroup>
                                                        <Input
                                                            type="text"
                                                            id="searchMessage"
                                                            placeholder="Search messages..."
                                                            onChange={handelSearch}
                                                        />
                                                    </FormGroup>
                                                </Form>
                                            </DropdownMenu>
                                        </Dropdown>
                                    </li>
                                    <li className="list-inline-item">
                                        <Dropdown isOpen={other_Menu} toggle={toggleOther}>
                                            <DropdownToggle className="btn nav-btn" tag="a">
                                                <i className="bx bx-dots-horizontal-rounded" />
                                            </DropdownToggle>
                                            <DropdownMenu className="dropdown-menu-end">
                                                <DropdownItem href="#"><i className="bx bxs-eraser me-2"></i>Clear</DropdownItem>
                                                <DropdownItem href="#"><i className="bx bx-share-alt me-2"></i>Share</DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                    </li>
                                </ul>
                            </Col>
                        </Row>
                    </div>
                    
                    <div>
                        <div className="chat-conversation p-3">
                            <SimpleBar ref={scrollRef} style={{ height: "470px" }}>
                                {isLoading ? (
                                    <Spinners setLoading={setLoading} />
                                ) : (
                                    <ul className="list-unstyled" id="users-conversation">
                                        {!currentSessionId ? (
                                            <li className="text-center text-muted py-4">
                                                <p>Select a chat to start messaging</p>
                                            </li>
                                        ) : messages.length === 0 ? (
                                            <li className="text-center text-muted py-4">
                                                <p>No messages yet. Start the conversation!</p>
                                            </li>
                                        ) : (
                                            messages.map((message: Message, index: number) => (
                                                <li key={message.id} className={message.is_from_user ? "right" : ""}>
                                                    <div className={`conversation-list ${message.is_from_user ? 'from-user' : 'from-ai'}`}>
                                                        <UncontrolledDropdown>
                                                            <DropdownToggle href="#!" tag="a" className="dropdown-toggle">
                                                                <i className="bx bx-dots-vertical-rounded" />
                                                            </DropdownToggle>
                                                            <DropdownMenu>
                                                                <DropdownItem onClick={(e) => copyMsg(e.target)} href="#">
                                                                    Copy
                                                                </DropdownItem>
                                                                <DropdownItem href="#">Save</DropdownItem>
                                                                <DropdownItem href="#">Forward</DropdownItem>
                                                            </DropdownMenu>
                                                        </UncontrolledDropdown>
                                                        <div className="ctext-wrap">
                                                            <div className="conversation-name text-capitalize">
                                                                {message.is_from_user ? "You" : (assistantName || "AI Assistant")}
                                                            </div>
                                                            {message.is_from_user ? (
                                                                <p className="message-text">{message.content}</p>
                                                            ) : (
                                                                <div className="message-text markdown-content">
                                                                    <ReactMarkdown>
                                                                        {normalizeMarkdown(message.content)}
                                                                    </ReactMarkdown>
                                                                </div>
                                                            )}
                                                            {message.file_attachment && (
                                                                <img src={message.file_attachment} alt="attachment" width="150px" />
                                                            )}
                                                            <p className="chat-time mb-0" style={{ whiteSpace: 'nowrap', width: 'auto', minWidth: 60 }}>
                                                                <i className="bx bx-check-double align-middle me-1 text-primary" style={{ fontSize: '1.5em' }}></i>
                                                                {formatTime(message.timestamp)}
                                                                {message.is_edited && <span className="text-muted ms-1">(edited)</span>}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </li>
                                            ))
                                        )}
                                        
                                        {/* AI Typing Indicator */}
                                        {(isTyping || localTyping) && (
                                            <li>
                                                <div className="conversation-list">
                                                    <div className="ctext-wrap">
                                                        <div className="conversation-name text-capitalize">{assistantName || 'AI Assistant'}</div>
                                                        <div className="typing-indicator" style={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            gap: '6px', 
                                                            padding: '10px 16px', 
                                                            backgroundColor: '#f8f9fa', 
                                                            borderRadius: '12px', 
                                                            width: 'fit-content', 
                                                            minWidth: '80px',
                                                            margin: '8px 0', 
                                                            border: '1px solid #e9ecef' 
                                                        }}>
                                                            <span style={{ 
                                                                height: '10px', 
                                                                width: '10px', 
                                                                backgroundColor: '#6c757d', 
                                                                borderRadius: '50%', 
                                                                animation: 'typing 1.4s ease-in-out infinite', 
                                                                display: 'inline-block',
                                                                flexShrink: 0
                                                            }}></span>
                                                            <span style={{ 
                                                                height: '10px', 
                                                                width: '10px', 
                                                                backgroundColor: '#6c757d', 
                                                                borderRadius: '50%', 
                                                                animation: 'typing 1.4s ease-in-out infinite 0.2s', 
                                                                display: 'inline-block',
                                                                flexShrink: 0
                                                            }}></span>
                                                            <span style={{ 
                                                                height: '10px', 
                                                                width: '10px', 
                                                                backgroundColor: '#6c757d', 
                                                                borderRadius: '50%', 
                                                                animation: 'typing 1.4s ease-in-out infinite 0.4s', 
                                                                display: 'inline-block',
                                                                flexShrink: 0
                                                            }}></span>
                                                        </div>
                                                        <div style={{ fontSize: '10px', color: '#6c757d', marginTop: '4px' }}>
                                                            AI is typing...
                                                        </div>
                                                    </div>
                                                </div>
                                            </li>
                                        )}
                                    </ul>
                                )}
                            </SimpleBar>
                        </div>
                        

                        {copyMsgAlert && (
                            <UncontrolledAlert color='warning' role="alert" className="m-3">
                                Message copied
                            </UncontrolledAlert>
                        )}
                        
                        {/* Standardized Chat Input Section */}
                        <div className="chat-input-container border-top position-relative">
                            {/* Emoji Picker */}
                            {emoji && (
                                <div className="position-absolute" style={{ bottom: "100%", left: "10px", marginBottom: "10px", zIndex: 1050 }}>
                                    <div className="emoji-picker-container">
                                        <EmojiPicker onEmojiClick={onEmojiClick} width={350} height={400} />
                                    </div>
                                </div>
                            )}
                            <div className="p-3">
                                {/* Main Input Row */}
                                <div className="d-flex align-items-end gap-2">
                                    {/* Left Controls */}
                                    <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                        {/* Emoji Button */}
                                        <button
                                            ref={emojiButtonRef}
                                            type="button"
                                            className={`btn btn-icon btn-sm ${emoji ? 'btn-primary' : 'btn-light'}`}
                                            onClick={() => setEmoji(!emoji)}
                                            disabled={!currentSessionId}
                                            id="Emojitooltip"
                                            title="Add emoji"
                                            style={{ minWidth: "38px", minHeight: "38px" }}
                                        >
                                            <i className="mdi mdi-emoticon-happy-outline" style={{ fontSize: "18px", lineHeight: 1 }}></i>
                                            <UncontrolledTooltip placement="top" target="Emojitooltip">
                                                Add emoji
                                            </UncontrolledTooltip>
                                        </button>

                                        {/* File Upload Button - Disabled (Coming Soon) */}
                                        <button
                                            type="button"
                                            className="btn btn-icon btn-sm btn-light"
                                            disabled={true}
                                            id="Filetooltip"
                                            style={{ minWidth: "38px", minHeight: "38px" }}
                                        >
                                            <i className="mdi mdi-paperclip" style={{ fontSize: "18px", lineHeight: 1 }}></i>
                                            <UncontrolledTooltip placement="top" target="Filetooltip">
                                                File attachments (Coming soon)
                                            </UncontrolledTooltip>
                                        </button>

                                        {/* Voice Recording Button - Disabled (Coming Soon) */}
                                        <button
                                            type="button"
                                            className="btn btn-icon btn-sm btn-light"
                                            disabled={true}
                                            id="Voicetooltip"
                                            style={{ minWidth: "38px", minHeight: "38px" }}
                                        >
                                            <i 
                                                className="mdi mdi-microphone" 
                                                style={{ fontSize: "18px", lineHeight: 1 }}
                                            ></i>
                                            <UncontrolledTooltip placement="top" target="Voicetooltip">
                                                Voice messages (Coming soon)
                                            </UncontrolledTooltip>
                                        </button>
                                    </div>

                                    {/* Text Input Area */}
                                    <div className="flex-grow-1 position-relative">
                                        <textarea
                                            ref={textareaRef}
                                            value={curMessage}
                                            onKeyDown={onKeyPress}
                                            onChange={e => handleInputChange(e.target.value)}
                                            className="form-control chat-input-textarea"
                                            placeholder={currentSessionId ? "Type your message... (Ctrl+Enter to send)" : "Select a chat to start messaging"}
                                            disabled={!currentSessionId}
                                            rows={1}
                                            style={{
                                                resize: "none",
                                                overflowY: "auto",
                                                minHeight: "42px",
                                                maxHeight: "150px",
                                                paddingRight: "40px",
                                                paddingLeft: "12px",
                                                paddingTop: "10px",
                                                paddingBottom: "10px",
                                                lineHeight: "1.5"
                                            }}
                                        />
                                        {/* Character Count (optional, shown when typing) */}
                                        {curMessage.length > 0 && (
                                            <small className="position-absolute bottom-0 end-0 me-2 mb-1 text-muted" 
                                                   style={{ fontSize: "10px", pointerEvents: "none" }}>
                                                {curMessage.length}
                                            </small>
                                        )}
                                    </div>

                                    {/* Send Button */}
                                    <button
                                        type="button"
                                        className="btn btn-primary flex-shrink-0"
                                        disabled={!currentSessionId || !curMessage.trim() || !chatWebSocket.isConnected()}
                                        onClick={addMessage}
                                        id="Sendtooltip"
                                        title="Send message (Ctrl+Enter)"
                                        style={{
                                            width: "42px",
                                            height: "42px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "50%",
                                            padding: 0
                                        }}
                                    >
                                        <i className="mdi mdi-send" style={{ fontSize: "18px", lineHeight: 1 }}></i>
                                        <UncontrolledTooltip placement="top" target="Sendtooltip">
                                            Send message (Ctrl+Enter)
                                        </UncontrolledTooltip>
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </React.Fragment>
    );
};

export default UserBotChat;